import {
  callGmListPlayers,
  callGmResetPlayer,
  callGmSetPlayerBanned,
  type GmPlayerRow,
} from "@/cloud/game-callables";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatWhen(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  try {
    return new Date(ms).toLocaleString("th-TH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function shortUid(uid: string): string {
  if (uid.length <= 12) return uid;
  return `${uid.slice(0, 6)}…${uid.slice(-4)}`;
}

function playerRowHtml(row: GmPlayerRow): string {
  const banLabel = row.disabled ? "ปลดแบน" : "แบน";
  const banClass = row.disabled ? "btn btn-ghost btn-sm" : "btn btn-danger btn-sm";
  const statusBadge = row.disabled
    ? `<span class="gm-users-badge gm-users-badge--banned">แบน</span>`
    : `<span class="gm-users-badge gm-users-badge--ok">ปกติ</span>`;
  const email = row.email ? escapeHtml(row.email) : "—";
  return `
    <tr data-gm-user-row="${escapeHtml(row.uid)}">
      <td>
        <div class="gm-users-name">${escapeHtml(row.displayName)}</div>
        <div class="gm-users-meta hint">${email}</div>
        <div class="gm-users-meta hint" title="${escapeHtml(row.uid)}">UID ${escapeHtml(shortUid(row.uid))}</div>
      </td>
      <td class="gm-users-num">${row.towerHighestCleared}</td>
      <td class="gm-users-num">${row.wealthCoins.toLocaleString("en-US")}</td>
      <td class="gm-users-num">${row.raidRankPoints.toLocaleString("en-US")}</td>
      <td>${statusBadge}</td>
      <td class="hint gm-users-when">${escapeHtml(formatWhen(row.lastSignInAt))}</td>
      <td class="gm-users-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-gm-user-reset="${escapeHtml(row.uid)}">รีเซ็ต</button>
        <button type="button" class="${banClass}" data-gm-user-ban="${escapeHtml(row.uid)}" data-gm-user-banned="${row.disabled ? "1" : "0"}">${banLabel}</button>
      </td>
    </tr>
  `;
}

export function renderGmUsersSectionHtml(): string {
  return `
    <p class="hint gm-settings-lead">
      รายชื่อบัญชี Firebase Auth · สถิติจาก <code>publicStats</code> · รีเซ็ตลบเซฟคลาวด์และเพิกถอนเซสชัน · แบนปิดการล็อกอิน
    </p>
    <section class="gm-users-panel" aria-labelledby="gm-users-list-title">
      <div class="gm-users-toolbar">
        <h3 class="gm-settings-group-title" id="gm-users-list-title">ผู้เล่นในระบบ</h3>
        <button type="button" class="btn btn-ghost btn-sm" id="gm-users-reload">โหลดใหม่</button>
      </div>
      <p class="hint gm-users-load-status" id="gm-users-load-status" role="status">กำลังโหลด…</p>
      <div class="gm-users-table-wrap">
        <table class="gm-users-table">
          <thead>
            <tr>
              <th>ผู้เล่น</th>
              <th>หอ</th>
              <th>ทอง</th>
              <th>แต้มโจมตี</th>
              <th>สถานะ</th>
              <th>ล็อกอินล่าสุด</th>
              <th>การทำงาน</th>
            </tr>
          </thead>
          <tbody id="gm-users-tbody"></tbody>
        </table>
      </div>
      <div class="gm-settings-actions gm-users-pager">
        <button type="button" class="btn btn-ghost" id="gm-users-more" hidden>โหลดเพิ่ม</button>
      </div>
      <p class="hint gm-settings-foot" id="gm-users-action-status" role="status"></p>
    </section>
  `;
}

export function bindGmUsersSection(root: HTMLElement): () => void {
  const tbody = () => root.querySelector<HTMLElement>("#gm-users-tbody");
  const loadStatus = () => root.querySelector<HTMLElement>("#gm-users-load-status");
  const actionStatus = () => root.querySelector<HTMLElement>("#gm-users-action-status");
  const moreBtn = () => root.querySelector<HTMLButtonElement>("#gm-users-more");

  let nextPageToken: string | null = null;
  let loading = false;
  let rows: GmPlayerRow[] = [];

  const setLoadStatus = (msg: string) => {
    const el = loadStatus();
    if (el) el.textContent = msg;
  };

  const setActionStatus = (msg: string, isError = false) => {
    const el = actionStatus();
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle("gm-control-status--error", isError);
  };

  const renderRows = () => {
    const body = tbody();
    if (!body) return;
    body.innerHTML = rows.map((r) => playerRowHtml(r)).join("");
  };

  const updatePager = () => {
    const btn = moreBtn();
    if (!btn) return;
    btn.hidden = !nextPageToken;
  };

  const loadPage = async (append: boolean) => {
    if (loading) return;
    loading = true;
    setLoadStatus(append ? "กำลังโหลดเพิ่ม…" : "กำลังโหลด…");
    setActionStatus("");
    try {
      const res = await callGmListPlayers({
        pageToken: append ? nextPageToken : null,
        limit: 50,
      });
      nextPageToken = res.nextPageToken;
      if (append) {
        const seen = new Set(rows.map((r) => r.uid));
        for (const p of res.players) {
          if (!seen.has(p.uid)) rows.push(p);
        }
      } else {
        rows = res.players;
      }
      renderRows();
      updatePager();
      setLoadStatus(`แสดง ${rows.length} คน${nextPageToken ? " · มีหน้าถัดไป" : ""}`);
    } catch (err) {
      setLoadStatus("");
      setActionStatus(err instanceof Error ? err.message : "โหลดรายชื่อไม่สำเร็จ", true);
    } finally {
      loading = false;
    }
  };

  const findRow = (uid: string): GmPlayerRow | undefined => rows.find((r) => r.uid === uid);

  const onReset = async (uid: string) => {
    const row = findRow(uid);
    const label = row?.displayName ?? uid;
    if (
      !window.confirm(
        `รีเซ็ตข้อมูลคลาวด์ของ "${label}"?\n\nลบเซฟตัวละคร/เกม/บ้าน/สกิล · รีเซ็ต publicStats · ยกเลิกเซสชัน\n\nผู้เล่นต้องเล่นใหม่จากข้อมูลคลาวด์ — ไม่ย้อนกลับ`,
      )
    ) {
      return;
    }
    setActionStatus("กำลังรีเซ็ต…");
    try {
      await callGmResetPlayer(uid);
      setActionStatus(`รีเซ็ต ${label} แล้ว`);
      await loadPage(false);
    } catch (err) {
      setActionStatus(err instanceof Error ? err.message : "รีเซ็ตไม่สำเร็จ", true);
    }
  };

  const onBanToggle = async (uid: string, currentlyBanned: boolean) => {
    const row = findRow(uid);
    const label = row?.displayName ?? uid;
    const nextBanned = !currentlyBanned;
    const verb = nextBanned ? "แบน" : "ปลดแบน";
    if (!window.confirm(`${verb} "${label}"?${nextBanned ? "\n\nผู้เล่นจะล็อกอินไม่ได้จนกว่าจะปลดแบน" : ""}`)) {
      return;
    }
    setActionStatus(`กำลัง${verb}…`);
    try {
      await callGmSetPlayerBanned(uid, nextBanned);
      setActionStatus(`${verb} ${label} แล้ว`);
      const target = findRow(uid);
      if (target) target.disabled = nextBanned;
      renderRows();
    } catch (err) {
      setActionStatus(err instanceof Error ? err.message : `${verb}ไม่สำเร็จ`, true);
    }
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "gm-users-reload" || target.closest("#gm-users-reload")) {
      void loadPage(false);
      return;
    }
    if (target.id === "gm-users-more" || target.closest("#gm-users-more")) {
      void loadPage(true);
      return;
    }
    const resetBtn = target.closest<HTMLElement>("[data-gm-user-reset]");
    if (resetBtn) {
      const uid = resetBtn.getAttribute("data-gm-user-reset");
      if (uid) void onReset(uid);
      return;
    }
    const banBtn = target.closest<HTMLElement>("[data-gm-user-ban]");
    if (banBtn) {
      const uid = banBtn.getAttribute("data-gm-user-ban");
      const banned = banBtn.getAttribute("data-gm-user-banned") === "1";
      if (uid) void onBanToggle(uid, banned);
    }
  };

  root.addEventListener("click", onClick);
  void loadPage(false);

  return () => root.removeEventListener("click", onClick);
}
